import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { Loader2, Check, Lock, ArrowRight, AlertTriangle, RotateCcw, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { ACCOUNT_SERIES } from '@/lib/accountSeries';


interface NearbyAccount {
  number: string;
  account_id: string;
  taken: boolean;
  account_name?: string;
}

export interface SubAccountConfig {
  parentAccountId: string;
  parentAccountName: string;
  mode: 'sub-account' | 'traveller' | 'ooa';
}

interface CRMAccountNumberPickerProps {
  value: string;
  onChange: (accountId: string, seriesType: string, billedStatus: string) => void;
  disabled?: boolean;
  editMode?: boolean;
  subAccountConfig?: SubAccountConfig | null;
}
const SERIES_OPTIONS = [
  ...ACCOUNT_SERIES.map((s) => ({
    value: s.type,
    label: `${s.label} (${s.key} Series)`,
    prefix: s.key,
    description: s.description,
  })),
  { value: 'Non-Billed', label: 'Non-Billed', prefix: '', description: 'Non-billed accounts (manual ID entry)' },
];

/**
 * Auto-detect the account series type and billed status from an account ID.
 * Handles base IDs (e.g. "7826"), sub-accounts (e.g. "7826-48"), and OoA (e.g. "5013-OoA").
 */
export const detectSeriesFromAccountId = (accountId: string): { seriesType: string; billedStatus: string } => {
  if (!accountId || !accountId.trim()) return { seriesType: '', billedStatus: '' };

  // Strip any suffix (-XX, -OoA, etc.) to get the base numeric part
  const baseId = accountId.trim().split('-')[0];

  // Check if the base is numeric
  if (/^\d+$/.test(baseId) && baseId.length >= 4) {
    const firstDigit = baseId.charAt(0);
    const match = SERIES_OPTIONS.find((s) => s.prefix === firstDigit);
    if (match && match.value !== 'Non-Billed') {
      return { seriesType: match.value, billedStatus: 'Billed' };
    }
  }

  return { seriesType: '', billedStatus: '' };
};



const CRMAccountNumberPicker: React.FC<CRMAccountNumberPickerProps> = ({
  value,
  onChange,
  disabled,
  editMode,
  subAccountConfig,
}) => {
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [suggestedNumber, setSuggestedNumber] = useState<string | null>(null);
  const [nearbyAccounts, setNearbyAccounts] = useState<NearbyAccount[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [manualId, setManualId] = useState('');

  // Gap / backfill state
  const [gaps, setGaps] = useState<number[]>([]);
  const [showGaps, setShowGaps] = useState(false);
  const [pickMode, setPickMode] = useState<'next' | 'backfill'>('next');

  // Manual backfill lookup state
  const [backfillManualInput, setBackfillManualInput] = useState('');
  const [backfillManualChecking, setBackfillManualChecking] = useState(false);
  const [backfillManualResult, setBackfillManualResult] = useState<{ available: boolean; accountName?: string } | null>(null);


  // Sub-account state
  const [subSuggested, setSubSuggested] = useState<string | null>(null);
  const [subNearby, setSubNearby] = useState<NearbyAccount[]>([]);
  const [loadingSub, setLoadingSub] = useState(false);

  // Edit mode state
  const [editingId, setEditingId] = useState(false);
  const [editIdValue, setEditIdValue] = useState('');
  const [checkingId, setCheckingId] = useState(false);
  const [idAvailable, setIdAvailable] = useState<boolean | null>(null);

  // OoA (Out of Area) toggle state for ACC series
  const [ooaEnabled, setOoaEnabled] = useState(false);
  const [ooaAvailable, setOoaAvailable] = useState<boolean | null>(null);
  const [checkingOoA, setCheckingOoA] = useState(false);


  // Convert a counter value to an account number for a given prefix
  const counterToAccount = useCallback((counter: number, prefix: string): number => {
    if (counter < 0) return parseInt(prefix) * 1000;
    const suffix = counter < 1000 ? String(counter).padStart(3, '0') : String(counter);
    return parseInt(prefix + suffix, 10);
  }, []);

  // Convert an account number back to its counter for a given prefix
  const accountToCounter = useCallback((accountNum: number, prefix: string): number => {
    const str = String(accountNum);
    if (!str.startsWith(prefix)) return -1;
    const suffix = str.slice(prefix.length);
    if (suffix.length === 0) return -1;
    return parseInt(suffix, 10);
  }, []);

  // Load sub-account data
  const loadSubAccountData = useCallback(async () => {
    if (!subAccountConfig) return;
    setLoadingSub(true);
    try {
      const parentId = subAccountConfig.parentAccountId;
      
      // Get all existing sub-accounts for this parent (matching pattern parentId-XX)
      const { data, error } = await supabase
        .from('crm_accounts')
        .select('account_id, account_name')
        .like('account_id', `${parentId}-%`)
        .order('account_id', { ascending: true });

      if (error) throw error;

      const existingMap = new Map<string, string>();
      const existingSuffixes: number[] = [];
      let hasOoA = false;

      (data || []).forEach((a) => {
        existingMap.set(a.account_id, a.account_name);
        const suffix = a.account_id.replace(`${parentId}-`, '');
        if (suffix === 'OoA') {
          hasOoA = true;
        } else {
          const num = parseInt(suffix, 10);
          if (!isNaN(num)) {
            existingSuffixes.push(num);
          }
        }
      });

      if (subAccountConfig.mode === 'ooa') {
        // OoA account
        const ooaId = `${parentId}-OoA`;
        if (existingMap.has(ooaId)) {
          setSubSuggested(null);
          setSubNearby([{
            number: ooaId,
            account_id: ooaId,
            taken: true,
            account_name: existingMap.get(ooaId),
          }]);
        } else {
          setSubSuggested(ooaId);
          setSubNearby([{
            number: ooaId,
            account_id: ooaId,
            taken: false,
          }]);
          setSelectedNumber(ooaId);
          const detectedOoA = detectSeriesFromAccountId(ooaId);
          onChange(ooaId, detectedOoA.seriesType, detectedOoA.billedStatus || 'Billed');
        }
      } else {

        // Sub-account or traveller - find next available number
        const maxSuffix = existingSuffixes.length > 0 ? Math.max(...existingSuffixes) : 0;
        const nextSuffix = maxSuffix + 1;
        const nextId = `${parentId}-${String(nextSuffix).padStart(2, '0')}`;

        setSubSuggested(nextId);

        // Build nearby list
        const nearby: NearbyAccount[] = [];
        const startSuffix = Math.max(1, nextSuffix - 3);
        for (let s = startSuffix; s <= nextSuffix + 3; s++) {
          const id = `${parentId}-${String(s).padStart(2, '0')}`;
          const taken = existingMap.has(id);
          nearby.push({
            number: id,
            account_id: id,
            taken,
            account_name: taken ? existingMap.get(id) : undefined,
          });
        }

        // Also show OoA if it exists
        if (hasOoA) {
          nearby.push({
            number: `${parentId}-OoA`,
            account_id: `${parentId}-OoA`,
            taken: true,
            account_name: existingMap.get(`${parentId}-OoA`),
          });
        }

        setSubNearby(nearby);
        setSelectedNumber(nextId);
        const detectedSub = detectSeriesFromAccountId(nextId);
        onChange(nextId, detectedSub.seriesType, detectedSub.billedStatus || 'Billed');

      }
    } catch (err) {
      console.error('Error loading sub-account data:', err);
    } finally {
      setLoadingSub(false);
    }
  }, [subAccountConfig, onChange]);

  useEffect(() => {
    if (subAccountConfig) {
      loadSubAccountData();
    }
  }, [subAccountConfig, loadSubAccountData]);

  const loadSeriesData = useCallback(async (series: string) => {
    const seriesOption = SERIES_OPTIONS.find((s) => s.value === series);
    if (!seriesOption || !seriesOption.prefix) return;

    setLoading(true);
    setSuggestedNumber(null);
    setNearbyAccounts([]);
    setSelectedNumber(null);
    setGaps([]);
    setPickMode('next');

    try {
      const prefix = seriesOption.prefix;

      // Paginate through ALL accounts in this series to avoid the 1000-row PostgREST default limit
      let allData: { account_id: string; account_name: string; account_number_numeric: number | null }[] = [];
      let offset = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('crm_accounts')
          .select('account_id, account_name, account_number_numeric')
          .like('account_id', `${prefix}%`)
          .order('account_number_numeric', { ascending: true })
          .range(offset, offset + pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allData = allData.concat(data);
          offset += data.length;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // Build a map of actual account numbers → names for "taken" checks
      const existingMap = new Map<number, string>();
      (allData || []).forEach((a) => {
        if (a.account_number_numeric != null) {
          existingMap.set(a.account_number_numeric, a.account_name);
        }
      });

      // Convert all existing account numbers to counters within this series
      const counters = (allData || [])
        .filter((a) => a.account_number_numeric != null)
        .map((a) => accountToCounter(a.account_number_numeric!, prefix))
        .filter((c) => c >= 0)
        .sort((a, b) => a - b);

      // Find gaps in the sequence
      const detectedGaps: number[] = [];
      if (counters.length > 0) {
        const minCounter = counters[0];
        const maxCounter = counters[counters.length - 1];
        const counterSet = new Set(counters);
        for (let c = minCounter; c <= maxCounter; c++) {
          if (!counterSet.has(c)) {
            detectedGaps.push(c);
          }
        }
      }
      setGaps(detectedGaps);

      // Find the highest counter
      const maxCounter = counters.length > 0 ? Math.max(...counters) : -1;
      const nextCounter = maxCounter + 1;

      // Convert the next counter to an account number
      const nextNumber = counterToAccount(nextCounter, prefix);
      const nextNumberStr = String(nextNumber);

      setSuggestedNumber(nextNumberStr);

      // Build nearby accounts list: 3 before suggested counter, suggested, 3 after
      const nearby: NearbyAccount[] = [];
      for (let c = nextCounter - 3; c <= nextCounter + 3; c++) {
        if (c < 0) continue;
        const accNum = counterToAccount(c, prefix);
        const taken = existingMap.has(accNum);
        nearby.push({
          number: String(accNum),
          account_id: String(accNum),
          taken,
          account_name: taken ? existingMap.get(accNum) : undefined,
        });
      }

      setNearbyAccounts(nearby);
      setSelectedNumber(nextNumberStr);

      // Auto-set the value
      onChange(nextNumberStr, series, series === 'Non-Billed' ? 'Non-Billed' : 'Billed');
    } catch (err) {
      console.error('Error loading series data:', err);
    } finally {
      setLoading(false);
    }
  }, [onChange, counterToAccount, accountToCounter]);


  const handlePickGap = (gapCounter: number) => {
    const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
    if (!seriesOption || !seriesOption.prefix) return;
    const accNum = counterToAccount(gapCounter, seriesOption.prefix);
    const accStr = String(accNum);
    setSelectedNumber(accStr);
    onChange(accStr, selectedSeries, 'Billed');
  };

  const handleSeriesChange = (series: string) => {
    setSelectedSeries(series);
    setOoaEnabled(false);
    setOoaAvailable(null);
    if (series === 'Non-Billed') {
      setNearbyAccounts([]);
      setSuggestedNumber(null);
      setSelectedNumber(null);
      setManualId('');
      setGaps([]);
    } else {
      loadSeriesData(series);
    }
  };

  const handleSelectNumber = (id: string) => {
    const nearby = [...nearbyAccounts, ...subNearby].find((n) => n.account_id === id);
    if (nearby?.taken) return;
    setSelectedNumber(id);
    setOoaEnabled(false);
    setOoaAvailable(null);
    if (subAccountConfig) {
      // For sub-accounts, auto-detect series from the account ID
      const detectedSelect = detectSeriesFromAccountId(id);
      onChange(id, detectedSelect.seriesType, detectedSelect.billedStatus || 'Billed');
    } else {
      const billed = selectedSeries === 'Non-Billed' ? 'Non-Billed' : 'Billed';
      onChange(id, selectedSeries, billed);
    }
  };



  const handleManualIdChange = (val: string) => {
    setManualId(val);
    onChange(val, 'Non-Billed', 'Non-Billed');
  };

  // Edit mode: check if new ID is available
  const checkIdAvailability = useCallback(async (newId: string) => {
    if (!newId.trim() || newId.trim() === value) {
      setIdAvailable(null);
      return;
    }
    setCheckingId(true);
    try {
      const { data, error } = await supabase
        .from('crm_accounts')
        .select('account_id')
        .eq('account_id', newId.trim())
        .limit(1);
      if (error) throw error;
      setIdAvailable(!data || data.length === 0);
    } catch {
      setIdAvailable(null);
    } finally {
      setCheckingId(false);
    }
  }, [value]);

  useEffect(() => {
    if (editingId && editIdValue.trim()) {
      const timer = setTimeout(() => checkIdAvailability(editIdValue), 400);
      return () => clearTimeout(timer);
    }
  }, [editIdValue, editingId, checkIdAvailability]);

  const handleConfirmEditId = () => {
    if (!editIdValue.trim() || !idAvailable) return;
    // Auto-detect series from the new account ID
    const detected = detectSeriesFromAccountId(editIdValue.trim());
    onChange(editIdValue.trim(), detected.seriesType, detected.billedStatus);
    setEditingId(false);
  };


  // ============================================================
  // SUB-ACCOUNT MODE
  // ============================================================
  if (subAccountConfig) {
    return (
      <div className="space-y-4">
        <div className="bg-secondary/30 border border-border rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">
            Creating {subAccountConfig.mode === 'ooa' ? 'Out of Area account' : subAccountConfig.mode === 'traveller' ? 'traveller account' : 'sub-account'} for:
          </p>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">{subAccountConfig.parentAccountId}</Badge>
            <span className="text-sm text-foreground font-medium">{subAccountConfig.parentAccountName}</span>
          </div>
        </div>

        {loadingSub ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Finding next available sub-account number...
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-foreground text-sm">Select Account Number</Label>

            {subSuggested && (
              <div className="flex items-center gap-2 bg-green-900/20 border border-green-700/40 rounded-lg px-3 py-2">
                <ArrowRight className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-300">
                  Suggested: <span className="font-mono font-bold">{subSuggested}</span>
                </span>
              </div>
            )}

            {!subSuggested && subAccountConfig.mode === 'ooa' && (
              <div className="flex items-center gap-2 bg-red-900/20 border border-red-700/40 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-300">
                  OoA account already exists for this parent.
                </span>
              </div>
            )}

            <div className="space-y-1">
              {subNearby.map((na) => {
                const isSuggested = na.account_id === subSuggested;
                const isSelected = na.account_id === selectedNumber;

                return (
                  <button
                    key={na.account_id}
                    type="button"
                    disabled={na.taken}
                    onClick={() => handleSelectNumber(na.account_id)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-all
                      ${na.taken
                        ? 'border-border/50 bg-secondary/20 opacity-50 cursor-not-allowed'
                        : isSelected
                          ? 'border-yellow-500 bg-yellow-900/30 ring-1 ring-yellow-500/40'
                          : isSuggested
                            ? 'border-green-600/60 bg-green-900/15 hover:bg-green-900/30 cursor-pointer'
                            : 'border-border bg-secondary/30 hover:bg-secondary/50 cursor-pointer'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`font-mono font-bold ${na.taken ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {na.account_id}
                      </span>
                      {na.taken && na.account_name && (
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {na.account_name}
                        </span>
                      )}
                      {isSuggested && !na.taken && (
                        <Badge className="bg-green-900/50 text-green-400 border-green-700 text-xs">
                          Suggested
                        </Badge>
                      )}
                      {na.account_id.endsWith('-OoA') && (
                        <Badge className="bg-orange-900/50 text-orange-400 border-orange-700 text-xs">
                          Out of Area
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {na.taken ? (
                        <Badge variant="secondary" className="text-xs bg-secondary text-muted-foreground">
                          <Lock className="w-3 h-3 mr-1" />
                          Taken
                        </Badge>
                      ) : isSelected ? (
                        <Check className="w-4 h-4 text-yellow-400" />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedNumber && (
              <p className="text-xs text-muted-foreground">
                Selected account ID: <span className="font-mono text-foreground font-medium">{selectedNumber}</span>
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // EDIT MODE - Allow changing account ID
  // ============================================================
  if (editMode) {
    return (
      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">Account ID</Label>
        {!editingId ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-secondary/50 border border-border rounded-md px-3 py-2 text-sm text-foreground font-mono flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-muted-foreground" />
              {value}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-yellow-600/60 text-yellow-400 hover:bg-yellow-900/30 text-xs"
              onClick={() => { setEditingId(true); setEditIdValue(value); setIdAvailable(null); }}
            >
              Change ID
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-yellow-900/20 border border-yellow-700/40 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              <p className="text-xs text-yellow-300">
                Changing the Account ID will update TaxiCaller job references. Use with caution.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  value={editIdValue}
                  onChange={(e) => setEditIdValue(e.target.value)}
                  className="dark-input font-mono pr-8"
                  placeholder="New account ID..."
                />
                {checkingId && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {!checkingId && idAvailable === true && editIdValue.trim() !== value && (
                  <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-400" />
                )}
                {!checkingId && idAvailable === false && (
                  <AlertTriangle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                )}
              </div>
              <Button
                type="button"
                size="sm"
                className="golden-button text-xs"
                disabled={!idAvailable || checkingId || !editIdValue.trim()}
                onClick={handleConfirmEditId}
              >
                Apply
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-xs text-muted-foreground"
                onClick={() => { setEditingId(false); setEditIdValue(''); setIdAvailable(null); }}
              >
                Cancel
              </Button>
            </div>
            {idAvailable === false && (
              <p className="text-xs text-red-400">This Account ID is already taken.</p>
            )}
            {idAvailable === true && editIdValue.trim() !== value && (
              <p className="text-xs text-green-400">This Account ID is available.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // DISABLED MODE (legacy)
  // ============================================================
  if (disabled) {
    return (
      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">Account ID</Label>
        <div className="bg-secondary/50 border border-border rounded-md px-3 py-2 text-sm text-foreground font-mono flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-muted-foreground" />
          {value}
        </div>
        <p className="text-xs text-muted-foreground">Account ID cannot be changed after creation.</p>
      </div>
    );
  }

  // ============================================================
  // CREATE MODE (normal)
  // ============================================================
  return (
    <div className="space-y-4">
      {/* Series Selection */}
      <div className="space-y-1.5">
        <Label className="text-foreground text-sm">
          Account Series <span className="text-red-400">*</span>
        </Label>
        <Select value={selectedSeries} onValueChange={handleSeriesChange}>
          <SelectTrigger className="dark-input">
            <SelectValue placeholder="Select account series..." />
          </SelectTrigger>
          <SelectContent>
            {SERIES_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2">
                  {opt.prefix && (
                    <Badge variant="outline" className="text-xs font-mono px-1.5 py-0">
                      {opt.prefix}xxx
                    </Badge>
                  )}
                  {opt.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedSeries && (
          <p className="text-xs text-muted-foreground">
            {SERIES_OPTIONS.find((s) => s.value === selectedSeries)?.description}
          </p>
        )}
      </div>

      {/* Non-Billed manual entry */}
      {selectedSeries === 'Non-Billed' && (
        <div className="space-y-1.5">
          <Label className="text-foreground text-sm">
            Account ID <span className="text-red-400">*</span>
          </Label>
          <input
            type="text"
            value={manualId}
            onChange={(e) => handleManualIdChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-border bg-secondary/50 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-500/60"
            placeholder="Enter account ID..."
          />
          <p className="text-xs text-muted-foreground">
            Non-billed accounts can have any ID format.
          </p>
        </div>
      )}

      {/* Loading */}
      {loading && selectedSeries && selectedSeries !== 'Non-Billed' && (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Finding next available number...
        </div>
      )}

      {/* Number picker */}
      {!loading && selectedSeries && selectedSeries !== 'Non-Billed' && nearbyAccounts.length > 0 && (
        <div className="space-y-2">
          <Label className="text-foreground text-sm">
            Select Account Number
          </Label>

          {/* Mode toggle: Next vs Backfill */}
          {gaps.length > 0 && (
            <div className="flex items-center gap-2 bg-secondary/30 border border-border rounded-lg p-2">
              <Button
                type="button"
                size="sm"
                variant={pickMode === 'next' ? 'default' : 'ghost'}
                className={`text-xs h-7 ${pickMode === 'next' ? 'golden-button' : 'text-muted-foreground'}`}
                onClick={() => {
                  setPickMode('next');
                  if (suggestedNumber) {
                    setSelectedNumber(suggestedNumber);
                    onChange(suggestedNumber, selectedSeries, 'Billed');
                  }
                }}
              >
                <ArrowRight className="w-3 h-3 mr-1" />
                Next Available
              </Button>
              <Button
                type="button"
                size="sm"
                variant={pickMode === 'backfill' ? 'default' : 'ghost'}
                className={`text-xs h-7 ${pickMode === 'backfill' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-muted-foreground'}`}
                onClick={() => setPickMode('backfill')}
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Backfill Gap ({gaps.length} available)
              </Button>
            </div>
          )}

          {/* NEXT MODE */}
          {pickMode === 'next' && (
            <>
              {suggestedNumber && (
                <div className="flex items-center gap-2 bg-green-900/20 border border-green-700/40 rounded-lg px-3 py-2">
                  <ArrowRight className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-300">
                    Suggested next available: <span className="font-mono font-bold">{suggestedNumber}</span>
                  </span>
                </div>
              )}

              <div className="space-y-1">
                {nearbyAccounts.map((na) => {
                  const isSuggested = na.account_id === suggestedNumber;
                  const isSelected = na.account_id === selectedNumber;

                  return (
                    <button
                      key={na.account_id}
                      type="button"
                      disabled={na.taken}
                      onClick={() => handleSelectNumber(na.account_id)}
                      className={`
                        w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-all
                        ${na.taken
                          ? 'border-border/50 bg-secondary/20 opacity-50 cursor-not-allowed'
                          : isSelected
                            ? 'border-yellow-500 bg-yellow-900/30 ring-1 ring-yellow-500/40'
                            : isSuggested
                              ? 'border-green-600/60 bg-green-900/15 hover:bg-green-900/30 cursor-pointer'
                              : 'border-border bg-secondary/30 hover:bg-secondary/50 cursor-pointer'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-mono font-bold ${na.taken ? 'text-muted-foreground' : 'text-foreground'}`}>
                          {na.account_id}
                        </span>
                        {na.taken && na.account_name && (
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {na.account_name}
                          </span>
                        )}
                        {isSuggested && !na.taken && (
                          <Badge className="bg-green-900/50 text-green-400 border-green-700 text-xs">
                            Suggested
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {na.taken ? (
                          <Badge variant="secondary" className="text-xs bg-secondary text-muted-foreground">
                            <Lock className="w-3 h-3 mr-1" />
                            Taken
                          </Badge>
                        ) : isSelected ? (
                          <Check className="w-4 h-4 text-yellow-400" />
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* BACKFILL MODE */}
          {pickMode === 'backfill' && (
            <>
              <div className="flex items-center gap-2 bg-blue-900/20 border border-blue-700/40 rounded-lg px-3 py-2">
                <RotateCcw className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-300">
                  {gaps.length} gap{gaps.length !== 1 ? 's' : ''} found in the sequence. Select one to backfill.
                </span>
              </div>

              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {gaps.map((gapCounter) => {
                  const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                  const accNum = seriesOption ? counterToAccount(gapCounter, seriesOption.prefix) : 0;
                  const accStr = String(accNum);
                  const isSelected = accStr === selectedNumber;

                  return (
                    <button
                      key={gapCounter}
                      type="button"
                      onClick={() => handlePickGap(gapCounter)}
                      className={`
                        w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition-all
                        ${isSelected
                          ? 'border-yellow-500 bg-yellow-900/30 ring-1 ring-yellow-500/40'
                          : 'border-blue-700/40 bg-blue-900/10 hover:bg-blue-900/20 cursor-pointer'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-foreground">{accStr}</span>
                        <Badge className="bg-blue-900/50 text-blue-400 border-blue-700 text-xs">
                          Gap
                        </Badge>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-yellow-400" />}
                    </button>
                  );
                })}
              </div>

              {/* Manual number lookup for backfill */}
              <div className="border border-border rounded-lg p-3 bg-secondary/20 space-y-2 mt-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Check a specific number
                </p>
                <p className="text-xs text-muted-foreground">
                  Enter a number to check if it's available for backfilling.
                </p>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      value={backfillManualInput}
                      onChange={(e) => {
                        setBackfillManualInput(e.target.value);
                        setBackfillManualResult(null);
                      }}
                      className="dark-input font-mono pr-8"
                      placeholder={(() => {
                        const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                        return seriesOption?.prefix ? `e.g. ${seriesOption.prefix}xxx` : 'Enter account number...';
                      })()}
                    />
                    {backfillManualChecking && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                    {!backfillManualChecking && backfillManualResult?.available === true && (
                      <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-400" />
                    )}
                    {!backfillManualChecking && backfillManualResult?.available === false && (
                      <Lock className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-xs border-blue-600/60 text-blue-400 hover:bg-blue-900/30"
                    disabled={!backfillManualInput.trim() || backfillManualChecking}
                    onClick={async () => {
                      const inputVal = backfillManualInput.trim();
                      if (!inputVal) return;
                      
                      // Build the full account ID - if user entered just a number, prepend the series prefix
                      const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                      let fullAccountId = inputVal;
                      if (seriesOption?.prefix && /^\d+$/.test(inputVal) && !inputVal.startsWith(seriesOption.prefix)) {
                        fullAccountId = seriesOption.prefix + inputVal.padStart(3, '0');
                      }
                      
                      setBackfillManualChecking(true);
                      setBackfillManualResult(null);
                      try {
                        const { data, error } = await supabase
                          .from('crm_accounts')
                          .select('account_id, account_name')
                          .eq('account_id', fullAccountId)
                          .limit(1);
                        if (error) throw error;
                        if (data && data.length > 0) {
                          setBackfillManualResult({ available: false, accountName: data[0].account_name });
                        } else {
                          setBackfillManualResult({ available: true });
                        }
                      } catch {
                        setBackfillManualResult(null);
                      } finally {
                        setBackfillManualChecking(false);
                      }
                    }}
                  >
                    Check
                  </Button>
                </div>

                {/* Result display */}
                {backfillManualResult && (
                  <div className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm ${
                    backfillManualResult.available
                      ? 'border-green-600/60 bg-green-900/15'
                      : 'border-red-600/40 bg-red-900/10'
                  }`}>
                    <div className="flex items-center gap-2">
                      {backfillManualResult.available ? (
                        <>
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-green-300">
                            <span className="font-mono font-bold">
                              {(() => {
                                const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                                const inputVal = backfillManualInput.trim();
                                if (seriesOption?.prefix && /^\d+$/.test(inputVal) && !inputVal.startsWith(seriesOption.prefix)) {
                                  return seriesOption.prefix + inputVal.padStart(3, '0');
                                }
                                return inputVal;
                              })()}
                            </span> is available
                          </span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 text-red-400" />
                          <span className="text-red-300">
                            <span className="font-mono font-bold">
                              {(() => {
                                const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                                const inputVal = backfillManualInput.trim();
                                if (seriesOption?.prefix && /^\d+$/.test(inputVal) && !inputVal.startsWith(seriesOption.prefix)) {
                                  return seriesOption.prefix + inputVal.padStart(3, '0');
                                }
                                return inputVal;
                              })()}
                            </span> is taken
                            {backfillManualResult.accountName && (
                              <span className="text-xs text-muted-foreground ml-1">({backfillManualResult.accountName})</span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {backfillManualResult.available && (
                      <Button
                        type="button"
                        size="sm"
                        className="golden-button text-xs h-7"
                        onClick={() => {
                          const seriesOption = SERIES_OPTIONS.find((s) => s.value === selectedSeries);
                          const inputVal = backfillManualInput.trim();
                          let fullAccountId = inputVal;
                          if (seriesOption?.prefix && /^\d+$/.test(inputVal) && !inputVal.startsWith(seriesOption.prefix)) {
                            fullAccountId = seriesOption.prefix + inputVal.padStart(3, '0');
                          }
                          setSelectedNumber(fullAccountId);
                          onChange(fullAccountId, selectedSeries, 'Billed');
                          setBackfillManualInput('');
                          setBackfillManualResult(null);
                        }}
                      >
                        Use This Number
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}


          {/* OoA (Out of Area) Toggle - ACC Series only */}
          {selectedSeries === 'ACC' && selectedNumber && !selectedNumber.includes('-') && (
            <OoAToggle
              baseNumber={selectedNumber}
              ooaEnabled={ooaEnabled}
              ooaAvailable={ooaAvailable}
              checkingOoA={checkingOoA}
              onToggle={async (enabled) => {
                setOoaEnabled(enabled);
                if (enabled) {
                  const ooaId = `${selectedNumber}-OoA`;
                  setCheckingOoA(true);
                  try {
                    const { data, error } = await supabase
                      .from('crm_accounts')
                      .select('account_id')
                      .eq('account_id', ooaId)
                      .limit(1);
                    if (error) throw error;
                    const available = !data || data.length === 0;
                    setOoaAvailable(available);
                    if (available) {
                      onChange(ooaId, selectedSeries, 'Billed');
                    }
                  } catch {
                    setOoaAvailable(null);
                  } finally {
                    setCheckingOoA(false);
                  }
                } else {
                  setOoaAvailable(null);
                  // Revert to the base number
                  onChange(selectedNumber, selectedSeries, 'Billed');
                }
              }}
            />
          )}

          {selectedNumber && (
            <p className="text-xs text-muted-foreground">
              Selected account ID: <span className="font-mono text-foreground font-medium">
                {ooaEnabled && selectedSeries === 'ACC' && !selectedNumber.includes('-') ? `${selectedNumber}-OoA` : selectedNumber}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// OoA Toggle sub-component
const OoAToggle: React.FC<{
  baseNumber: string;
  ooaEnabled: boolean;
  ooaAvailable: boolean | null;
  checkingOoA: boolean;
  onToggle: (enabled: boolean) => void;
}> = ({ baseNumber, ooaEnabled, ooaAvailable, checkingOoA, onToggle }) => {
  const ooaId = `${baseNumber}-OoA`;

  return (
    <div className={`rounded-lg border p-3 transition-all ${
      ooaEnabled
        ? 'border-orange-600/60 bg-orange-900/15'
        : 'border-border bg-secondary/20'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className={`w-4 h-4 ${ooaEnabled ? 'text-orange-400' : 'text-muted-foreground'}`} />
          <div>
            <Label className={`text-sm font-medium ${ooaEnabled ? 'text-orange-300' : 'text-foreground'}`}>
              Out of Area (OoA)
            </Label>
            <p className="text-xs text-muted-foreground">
              Create as an Out of Area account ({ooaId})
            </p>
          </div>
        </div>
        <Switch
          checked={ooaEnabled}
          onCheckedChange={onToggle}
        />
      </div>

      {ooaEnabled && (
        <div className="mt-2 pt-2 border-t border-orange-700/30">
          {checkingOoA ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Checking availability of {ooaId}...
            </div>
          ) : ooaAvailable === true ? (
            <div className="flex items-center gap-2 text-xs text-green-400">
              <Check className="w-3 h-3" />
              <span className="font-mono font-medium">{ooaId}</span> is available
            </div>
          ) : ooaAvailable === false ? (
            <div className="flex items-center gap-2 text-xs text-red-400">
              <AlertTriangle className="w-3 h-3" />
              <span className="font-mono font-medium">{ooaId}</span> already exists
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CRMAccountNumberPicker;
